import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  formatAfterSignedScanTimestamp,
  formatSignedScanScopeLabel,
  type AfterSignedScanTimelineChange,
  type AfterSignedScanTimelineMode,
} from '../../domain/incidents/afterSignedScanReview';
import {
  RISK_PATTERN_REVIEW_PAGE_COPY,
  formatSafetyCaseDisplayLabel,
  isWorkflowContextFetchErrorMessage,
  resolveRiskPatternReviewPageState,
  resolveRiskPatternReviewStateBPrimaryAction,
  stateBBodyCopyForPrimaryAction,
  type RiskPatternWorkflowReviewContext,
} from '../../domain/incidents/riskPatternWorkflowReviewContext';
import { computeNextSchedulePreview } from '../../domain/scheduledScans/scheduleRecurrencePreview';
import {
  scheduledScansApi,
  type ScheduledScanSchedule,
} from '../../services/scheduledScansApi';
import { AfterSignedScanTimelineSelector } from './AfterSignedScanTimelineSelector';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Compact loading shell — State C loads collapsed by default. */
const WORKFLOW_REVIEW_PANEL_LOADING_MIN_HEIGHT = 120;
const RECURRING_SCHEDULE_MIN_HEIGHT = 128;
const STATE_C_EXPANDED_STORAGE_PREFIX = 'ui.risk_pattern_review.state_c_expanded:';

function stateCExpandedStorageKey(workflowId: string): string {
  return `${STATE_C_EXPANDED_STORAGE_PREFIX}${String(workflowId || '').trim() || 'unknown'}`;
}

function readStateCExpandedPreference(workflowId: string): boolean {
  try {
    return window.localStorage.getItem(stateCExpandedStorageKey(workflowId)) === '1';
  } catch {
    return false;
  }
}

function writeStateCExpandedPreference(workflowId: string, expanded: boolean): void {
  try {
    window.localStorage.setItem(stateCExpandedStorageKey(workflowId), expanded ? '1' : '0');
  } catch {
    // Ignore storage failures (private mode / quota).
  }
}

function formatWindow(seconds: number): string {
  if (seconds % 86400 === 0) {
    const days = seconds / 86400;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  const hours = seconds / 3600;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hours`;
}

const RecurringScheduleSummary: React.FC<{ siteId: string; workflowId: string }> = ({
  siteId,
  workflowId,
}) => {
  const [status, setStatus] = React.useState<'loading' | 'visible' | 'hidden'>('loading');
  const [summary, setSummary] = React.useState<{
    definitionName: string;
    schedule: ScheduledScanSchedule;
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setSummary(null);
    void (async () => {
      if (!await scheduledScansApi.featureEnabled()) {
        if (!cancelled) setStatus('hidden');
        return;
      }
      const definitions = (await scheduledScansApi.listDefinitions(siteId))
        .filter((definition) => (
          definition.status === 'ACTIVE'
          && String(
            (definition.engine_config?.workflow_context as Record<string, unknown> | undefined)
              ?.source_workflow_id || '',
          ).trim() === workflowId
        ));
      const schedulesByDefinition = await Promise.all(definitions.map(async (definition) => ({
        definition,
        schedules: await scheduledScansApi.listSchedules(siteId, definition.scan_definition_id),
      })));
      const selected = schedulesByDefinition
        .map(({ definition, schedules }) => ({
          definition,
          schedule: schedules.find((schedule) => schedule.enabled && schedule.cadence !== 'OFF'),
        }))
        .find(({ schedule }) => Boolean(schedule));
      if (!cancelled) {
        setSummary(selected?.schedule ? {
          definitionName: selected.definition.name,
          schedule: selected.schedule,
        } : null);
        setStatus('visible');
      }
    })().catch(() => {
      if (!cancelled) setStatus('hidden');
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, workflowId]);

  if (status === 'hidden') return null;
  if (status === 'loading') {
    return (
      <Box
        data-testid="recurring-schedule-summary-loading"
        sx={{ mt: 1, minHeight: RECURRING_SCHEDULE_MIN_HEIGHT }}
        aria-busy="true"
      >
        <Skeleton variant="text" width="40%" height={22} />
        <Skeleton variant="text" width="78%" />
        <Skeleton variant="text" width="62%" />
        <Skeleton variant="text" width="48%" />
        <Skeleton variant="text" width="70%" />
      </Box>
    );
  }
  if (!summary) {
    return (
      <Box sx={{ mt: 1, minHeight: RECURRING_SCHEDULE_MIN_HEIGHT }}>
        <Typography variant="body2">Safety Scan schedule: Not configured</Typography>
      </Box>
    );
  }

  const { schedule, definitionName } = summary;
  let nextOccurrence = 'Unavailable';
  try {
    nextOccurrence = computeNextSchedulePreview({
      enabled: schedule.enabled,
      cadence: schedule.cadence,
      dayOfWeek: schedule.day_of_week ?? 0,
      executionTime: schedule.execution_time_local,
      timezone: schedule.timezone,
      windowSeconds: schedule.window_length_seconds,
    })?.occurrenceLabel || nextOccurrence;
  } catch {
    // A malformed persisted schedule must not block Risk Pattern review.
  }
  const cadence = schedule.cadence === 'WEEKLY'
    ? `Weekly on ${WEEKDAYS[schedule.day_of_week ?? 0]} at ${schedule.execution_time_local.slice(0, 5)}`
    : `Daily at ${schedule.execution_time_local.slice(0, 5)}`;

  return (
    <Box
      data-testid="recurring-schedule-summary"
      sx={{ mt: 1, minHeight: RECURRING_SCHEDULE_MIN_HEIGHT }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Safety Scan schedule</Typography>
      <Typography variant="body2">{`${definitionName}: ${cadence}`}</Typography>
      <Typography variant="body2">{`Next occurrence: ${nextOccurrence}`}</Typography>
      <Typography variant="body2">{`Timezone: ${schedule.timezone}`}</Typography>
      <Typography variant="body2">{`Observation window: Previous ${formatWindow(schedule.window_length_seconds)}`}</Typography>
      <Typography variant="caption" color="text.secondary">
        Repeats this Risk Pattern and scope using a relative observation window.
      </Typography>
    </Box>
  );
};

export type RiskPatternWorkflowReviewActions = {
  onReviewEvidence?: () => void;
  onCreateSafetyCase?: () => void;
  onMarkFalsePositive?: () => void;
  onNeedsMoreReview?: () => void;
  onOpenSafetyCase?: () => void;
  onContinueReview?: () => void;
  onOpenReportPreparation?: () => void;
  /** Kept for Report preparation / Next Required Action surfaces; not shown in the blue panel action row. */
  onCreateSafetyScan?: () => void;
  onConfigureScheduledScans?: () => void;
  onViewSignedSafetyScan?: () => void;
  onReviewNewActivity?: () => void;
  onCreateUpdatedSafetyScan?: () => void;
  /** Existing Scan episode-review path when Case + current Scan exist. */
  currentScanReviewPath?: string | null;
  /** Linked Case path when a Scan is missing; never the empty proof builder. */
  linkedSafetyCasePath?: string | null;
};

export type RiskPatternWorkflowReviewTimelineProps = {
  signedScanAt: string;
  timelineMode: AfterSignedScanTimelineMode;
  windowStartIso: string;
  windowEndIso?: string | null;
  onTimelineChange: (change: AfterSignedScanTimelineChange) => void;
  onResolveStartBeforeSignedScan: (action: 'full_history' | 'reset_start') => void;
};

export type RiskPatternWorkflowReviewContextPanelProps = {
  context?: RiskPatternWorkflowReviewContext | null;
  loading?: boolean;
  error?: string | null;
  actions?: RiskPatternWorkflowReviewActions;
  afterSignedScanTimeline?: RiskPatternWorkflowReviewTimelineProps | null;
  scanWindowLabel?: string | null;
  scheduledScanSiteId?: string | null;
  /** When true, State A next action can create a Safety Case. */
  evidenceConfirmed?: boolean;
  representativeReviewComplete?: boolean;
  siteTimeZone?: string | null;
};

export const RiskPatternWorkflowReviewContextPanel: React.FC<RiskPatternWorkflowReviewContextPanelProps> = ({
  context,
  loading = false,
  error = null,
  actions = {},
  afterSignedScanTimeline = null,
  scanWindowLabel = null,
  scheduledScanSiteId = null,
  evidenceConfirmed = false,
  representativeReviewComplete = false,
  siteTimeZone = null,
}) => {
  const workflowIdForPreference = String(context?.workflowId || '').trim();
  const [stateCExpanded, setStateCExpanded] = React.useState(false);

  React.useEffect(() => {
    if (!workflowIdForPreference) {
      setStateCExpanded(false);
      return;
    }
    setStateCExpanded(readStateCExpandedPreference(workflowIdForPreference));
  }, [workflowIdForPreference]);

  const setStateCExpandedAndRemember = React.useCallback((expanded: boolean) => {
    setStateCExpanded(expanded);
    if (workflowIdForPreference) {
      writeStateCExpandedPreference(workflowIdForPreference, expanded);
    }
  }, [workflowIdForPreference]);

  if (loading) {
    return (
      <Alert
        severity="info"
        data-testid="risk-pattern-workflow-review-context-panel"
        data-loading="true"
        aria-busy="true"
        sx={{ mb: 2, borderRadius: 2, minHeight: WORKFLOW_REVIEW_PANEL_LOADING_MIN_HEIGHT }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Loading workflow context for this Risk Pattern…
          </Typography>
          <Skeleton variant="text" width="48%" />
          <Skeleton variant="rounded" width={180} height={32} />
        </Stack>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert
        severity={isWorkflowContextFetchErrorMessage(error) ? 'warning' : 'info'}
        data-testid="risk-pattern-workflow-review-context-panel"
        data-workflow-context-state={isWorkflowContextFetchErrorMessage(error) ? 'fetch_error' : 'limited_mode'}
      >
        {error}
      </Alert>
    );
  }

  if (!context) {
    return null;
  }

  const pageState = resolveRiskPatternReviewPageState(context);
  const copy = RISK_PATTERN_REVIEW_PAGE_COPY[pageState];
  const safetyCaseLabel = formatSafetyCaseDisplayLabel(context);
  const stateBPrimary = pageState === 'state_b'
    ? resolveRiskPatternReviewStateBPrimaryAction({
      representativeReviewComplete,
      evidenceConfirmed,
      hasContinueReview: Boolean(actions.onContinueReview),
      hasOpenSafetyCase: Boolean(actions.onOpenSafetyCase),
    })
    : null;
  const bodyText = pageState === 'state_b' && stateBPrimary
    ? stateBBodyCopyForPrimaryAction(stateBPrimary)
    : copy.body;

  const renderLinkedSafetyCase = () => {
    if (actions.onOpenSafetyCase) {
      return (
        <Typography variant="body2" component="div">
          {'Linked Safety Case: '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={actions.onOpenSafetyCase}
            data-testid="risk-pattern-linked-safety-case-link"
            sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
          >
            {safetyCaseLabel}
          </Link>
        </Typography>
      );
    }
    return (
      <Typography variant="body2">
        {`Linked Safety Case: ${safetyCaseLabel}`}
      </Typography>
    );
  };

  const renderSoleNextAction = () => {
    if (pageState === 'state_a') {
      if (evidenceConfirmed && actions.onCreateSafetyCase) {
        return (
          <Button
            size="small"
            variant="contained"
            onClick={actions.onCreateSafetyCase}
            data-testid="risk-pattern-sole-next-action"
          >
            {RISK_PATTERN_REVIEW_PAGE_COPY.state_a.actions.createSafetyCase}
          </Button>
        );
      }
      if (!actions.onReviewEvidence) return null;
      return (
        <Button
          size="small"
          variant="contained"
          onClick={actions.onReviewEvidence}
          data-testid="risk-pattern-sole-next-action"
        >
          {RISK_PATTERN_REVIEW_PAGE_COPY.state_a.actions.reviewEvidence}
        </Button>
      );
    }

    if (pageState === 'state_b') {
      const actionCopy = RISK_PATTERN_REVIEW_PAGE_COPY.state_b.actions;
      if (stateBPrimary === 'open_safety_case' && actions.onOpenSafetyCase) {
        return (
          <Button
            size="small"
            variant="contained"
            onClick={actions.onOpenSafetyCase}
            data-testid="risk-pattern-sole-next-action"
          >
            {actionCopy.openSafetyCase}
          </Button>
        );
      }
      if (stateBPrimary === 'continue_review' && actions.onContinueReview) {
        return (
          <Button
            size="small"
            variant="contained"
            href={actions.currentScanReviewPath || undefined}
            onClick={(event) => {
              if (actions.currentScanReviewPath) event.preventDefault();
              actions.onContinueReview?.();
            }}
            data-testid="risk-pattern-sole-next-action"
          >
            {actionCopy.continueReview}
          </Button>
        );
      }
      return null;
    }

    return null;
  };

  const signedScanTimestampLabel = formatAfterSignedScanTimestamp(context.latestSignedScanAt, siteTimeZone);
  const renderStateCSignedScanStamp = () => (
    <Typography variant="body2" component="div" sx={{ mt: 0.35 }}>
      {'Latest Signed Safety Scan · '}
      {actions.onViewSignedSafetyScan ? (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={actions.onViewSignedSafetyScan}
          data-testid="risk-pattern-view-signed-scan-link"
          sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
        >
          {signedScanTimestampLabel}
        </Link>
      ) : (
        signedScanTimestampLabel
      )}
    </Typography>
  );

  if (pageState === 'state_c') {
    const actionCopy = RISK_PATTERN_REVIEW_PAGE_COPY.state_c.actions;
    return (
      <Alert
        severity="info"
        data-testid="risk-pattern-workflow-review-context-panel"
        data-review-page-state={pageState}
        data-state-c-expanded={stateCExpanded ? 'true' : 'false'}
        sx={{ mb: 2, borderRadius: 2 }}
      >
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {copy.title}
              </Typography>
              {renderStateCSignedScanStamp()}
            </Box>
            <IconButton
              size="small"
              onClick={() => setStateCExpandedAndRemember(!stateCExpanded)}
              aria-expanded={stateCExpanded}
              aria-label={stateCExpanded
                ? 'Hide window and schedule details'
                : 'Show window and schedule details'}
              data-testid="risk-pattern-state-c-expand-toggle"
              sx={{
                mt: -0.25,
                transform: stateCExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>

          {actions.onOpenSafetyCase ? (
            <Box>
              <Button
                size="small"
                variant="contained"
                onClick={actions.onOpenSafetyCase}
                data-testid="risk-pattern-sole-next-action"
              >
                {RISK_PATTERN_REVIEW_PAGE_COPY.state_b.actions.openSafetyCase}
              </Button>
            </Box>
          ) : actions.onReviewNewActivity ? (
            <Box>
              <Button
                size="small"
                variant="contained"
                href={actions.currentScanReviewPath || undefined}
                onClick={(event) => {
                  if (actions.currentScanReviewPath) event.preventDefault();
                  actions.onReviewNewActivity?.();
                }}
                data-testid="risk-pattern-sole-next-action"
              >
                {actionCopy.reviewNewActivity}
              </Button>
            </Box>
          ) : null}
          {actions.onOpenSafetyCase && actions.onReviewNewActivity ? (
            <Button
              size="small"
              variant="text"
              href={actions.currentScanReviewPath || undefined}
              onClick={(event) => {
                if (actions.currentScanReviewPath) event.preventDefault();
                actions.onReviewNewActivity?.();
              }}
              data-testid="risk-pattern-explore-observations"
              sx={{ alignSelf: 'flex-start', px: 0 }}
            >
              {actionCopy.reviewNewActivity}
            </Button>
          ) : null}

          {!stateCExpanded ? (
            <Button
              size="small"
              variant="text"
              onClick={() => setStateCExpandedAndRemember(true)}
              data-testid="risk-pattern-state-c-show-details"
              sx={{ alignSelf: 'flex-start', px: 0 }}
            >
              Show window & schedule
            </Button>
          ) : null}

          <Collapse in={stateCExpanded} unmountOnExit>
            <Stack spacing={1.25} sx={{ pt: 0.5 }}>
              <Typography variant="body2">
                {bodyText}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Show</Typography>
                {renderLinkedSafetyCase()}
                <Typography variant="body2">
                  {`Originating Risk Pattern: ${context.originatingRiskPattern || context.riskPatternSummary}`}
                </Typography>
                {context.caseStateLabel ? (
                  <Typography variant="body2">
                    {`Current case state: ${context.caseStateLabel}`}
                  </Typography>
                ) : null}
                <Typography variant="body2">
                  {`Latest Signed Safety Scan scope: ${formatSignedScanScopeLabel(context.signedScanScopeStart, context.signedScanScopeEnd, siteTimeZone)}`}
                </Typography>
                {afterSignedScanTimeline ? (
                  <Box sx={{ mt: 1.25 }}>
                    <AfterSignedScanTimelineSelector
                      signedScanAt={afterSignedScanTimeline.signedScanAt}
                      timelineMode={afterSignedScanTimeline.timelineMode}
                      windowStartIso={afterSignedScanTimeline.windowStartIso}
                      windowEndIso={afterSignedScanTimeline.windowEndIso}
                      onTimelineChange={afterSignedScanTimeline.onTimelineChange}
                      onResolveStartBeforeSignedScan={afterSignedScanTimeline.onResolveStartBeforeSignedScan}
                      siteTimeZone={siteTimeZone}
                    />
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2">
                      {`New activity window start: ${formatAfterSignedScanTimestamp(context.latestSignedScanAt, siteTimeZone)}`}
                    </Typography>
                    <Typography variant="body2">
                      {`New activity window end: ${context.latestObservationAt
                        ? formatAfterSignedScanTimestamp(context.latestObservationAt, siteTimeZone)
                        : 'now'}`}
                    </Typography>
                  </>
                )}
                <Typography variant="body2">
                  {`Latest observation: ${formatAfterSignedScanTimestamp(context.latestObservationAt, siteTimeZone)}`}
                </Typography>
                {actions.onConfigureScheduledScans && scheduledScanSiteId ? (
                  <RecurringScheduleSummary
                    siteId={scheduledScanSiteId}
                    workflowId={context.workflowId}
                  />
                ) : null}
              </Box>
              {actions.onConfigureScheduledScans ? (
                <Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={actions.onConfigureScheduledScans}
                    data-testid="risk-pattern-schedule-action"
                  >
                    Schedule this Safety Scan
                  </Button>
                </Box>
              ) : null}
            </Stack>
          </Collapse>
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      severity="info"
      data-testid="risk-pattern-workflow-review-context-panel"
      data-review-page-state={pageState}
      sx={{ mb: 2, borderRadius: 2 }}
    >
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {copy.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {bodyText}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Show</Typography>
          {pageState === 'state_a' ? (
            <>
              <Typography variant="body2">
                {`Safety Case: ${safetyCaseLabel}`}
              </Typography>
              <Typography variant="body2">
                {`Latest Safety Scan: ${RISK_PATTERN_REVIEW_PAGE_COPY.state_a.latestSafetyScanLabel}`}
              </Typography>
            </>
          ) : null}
          {pageState === 'state_b' ? (
            <>
              {renderLinkedSafetyCase()}
              <Typography variant="body2">
                {`Originating Risk Pattern: ${context.originatingRiskPattern || context.riskPatternSummary}`}
              </Typography>
              {context.caseStateLabel ? (
                <Typography variant="body2">
                  {`Current case state: ${context.caseStateLabel}`}
                </Typography>
              ) : null}
              <Typography variant="body2">
                {context.hasSignedSafetyScan && context.latestSignedScanAt
                  ? `Latest Signed Safety Scan: ${formatAfterSignedScanTimestamp(context.latestSignedScanAt, siteTimeZone)}`
                  : `Latest Signed Safety Scan: ${RISK_PATTERN_REVIEW_PAGE_COPY.state_b.safetyScanLabel}`}
              </Typography>
              {scanWindowLabel ? (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`Safety Scan window: ${scanWindowLabel}`}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Box>

        {renderSoleNextAction()}
        {pageState === 'state_b'
          && stateBPrimary === 'open_safety_case'
          && actions.onContinueReview ? (
          <Button
            size="small"
            variant="text"
            href={actions.currentScanReviewPath || undefined}
            onClick={(event) => {
              if (actions.currentScanReviewPath) event.preventDefault();
              actions.onContinueReview?.();
            }}
            data-testid="risk-pattern-explore-observations"
            sx={{ alignSelf: 'flex-start', px: 0 }}
          >
            {RISK_PATTERN_REVIEW_PAGE_COPY.state_b.actions.continueReview}
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
};
